import * as d3 from 'd3';
import { activityDetailStore } from '@client/store/activityDetailStore';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getDataFrom, NULL_TEXT } from './utils/common';
import unitHelper from '@client/helper/unit';
import { CHART_TYPE_HISTOGRAM } from '@client/pages/activity-detail/config/chart.types';
import { getSportAttrs } from './utils/getSportAttrs';
import { getChartAttr } from './utils/chart-render-config';
import Definition from '@client/config/definition';
import { useListenResize } from '@client/hooks/useBoardResizeEvent';
import { activeTab } from '../../hooks/useActivityTabs';
import { useActivityDetail } from '../../hooks/useActivityDetail';

interface ActiveAttribute {
  chartName: string;
  chartType: string;
  valueKey: string;
  data: Record<string, any>;
  defined: any;
  text: string;
  unit: string;
  graph: any;
  graphObjKey: string;
  color: string;
  subCharts: any;
  min: number;
  max: number;
  ticks: Array<{ value: number; text: string }>;
  tickText: () => string;
}

const clientWidth = ref(document.body.clientWidth);

// 放到这里就是所有 `useActiveAttributes` 的调用都共享了
const sportAttrs = ref([]);
const chartAttr = ref({});
const activeAttrNames = ref([]);
const getFrom = ref('');

export function useActiveAttributes({
  isOverview,
  setWatch,
  setListenResize
}: { isOverview?: boolean; setWatch?: boolean; setListenResize?: boolean } = {}) {
  const activityDetailStoreRef = activityDetailStore();
  const { unitType, graphObj, topSummary, curActivityDetail } = useActivityDetail();

  if (setListenResize) {
    const { cancelListner } = useListenResize(() => {
      clientWidth.value = document.body.clientWidth;
    });
    onBeforeUnmount(cancelListner);
  }

  const chartData = computed(() => {
    if (isOverview) {
      return activityDetailStoreRef.polylineData;
    }

    return activityDetailStoreRef.frameSelection?.data;
  });

  const chartType = computed(() => activityDetailStoreRef.chartType);

  // graphObjKey仅用作匹配y轴配置信息，graphObjKey的值要取频率数据中的字段名，否则接口返回的y轴信息对应不上
  // valueKey为取对应值的字段名
  // tickText的第二参数，仅当处理图表中数据才会传入，处理y轴时不传入

  const otherResult = computed(() => activityDetailStoreRef.otherResult);
  const speedUnit = computed(() => curActivityDetail.value?.summary?.speedUnit);
  const summary = computed(() => curActivityDetail.value?.summary);
  const sportType = computed(() => curActivityDetail.value?.summary?.sportType);
  const sportName = Definition.getSportNameByType(sportType.value);

  function updateSportAttrs() {
    if (!summary.value) return;
    // 获取改运动图表配置
    const val = getSportAttrs({
      getFrom: getFrom.value,
      topSummary: topSummary.value,
      chartAttr: chartAttr.value,
      summary: summary.value,
      graphObj: graphObj.value
    });
    // console.log(`val`, val);
    sportAttrs.value = val;
    if (!val?.[0]?.chartName) {
      console.error(`noChartName`);
      this.$trace.traceCustomEvent('noChartName', '');
    }
    updateActiveAttrNames([val?.[0]?.chartName]);
  }

  // 更新 chartAttr
  function updateChartArrs() {
    // console.log(`updateChartArrs otherResult.value`, otherResult.value, unitType.value);
    chartAttr.value = getChartAttr({
      chartType: chartType.value,
      getFrom: getFrom.value,
      otherResult: otherResult.value,
      graphObj: graphObj.value,
      thisSpeedUnit: speedUnit.value,
      sportName,
      sportType,
      unitType: unitType.value
    });
    // console.log('chartAttr ', chartAttr.value);
  }

  nextTick(() => {
    getFrom.value = getDataFrom(chartType.value);
  });

  if (setWatch) {
    nextTick(() => {
      setTimeout(() => {
        // console.log(`useActiveAttributes setWatch`, setWatch);
        watch(chartType, () => {
          // console.log(`chartType changed`, chartType.value);
          nextTick(() => {
            getFrom.value = getDataFrom(chartType.value);
            updateChartArrs();
            updateSportAttrs();
          });
        });
        // chartAttr 监听
        watch(
          () => otherResult.value?.showFormPower,
          () => {
            // console.log(`otherResult changed`, otherResult.value);
            nextTick(() => {
              updateChartArrs();
              updateSportAttrs();
            });
          }
        );
        // 监听 activeTab 变化，多段攀岩图表切换到其他子运动，同类型 sportType 切换也要更新（ticksInfo要变）
        watch(activeTab, () => {
          // console.log(`activeTab changed`, activeTab.value);
          nextTick(() => {
            setTimeout(() => {
              updateChartArrs();
              updateSportAttrs();
            }, 0);
          });
        });
      });
    });
  }
  function updateActiveAttrNames(val) {
    activeAttrNames.value = val;
  }
  // 当前被选中的属性
  const activeAttributes = computed<ActiveAttribute[]>(() => {
    if (!chartData.value?.length) {
      return [];
    }
    let result = activeAttrNames.value.map((chartName) => {
      for (const n of sportAttrs.value) {
        if (n.chartName === chartName) {
          return {
            ...n
          };
        }
      }
      return null;
    });

    result = result.filter((item) => !!item);
    if (result.length === 0) {
      return [];
    }

    // 图表为柱状图的时候
    if (chartType.value === 'index-rect') {
      result.forEach((attr) => {
        attr.chartType = CHART_TYPE_HISTOGRAM;
      });
    } else {
      // 配速和等价配速合并y轴
      (() => {
        const PaceIndex = activeAttrNames.value.indexOf('Pace');
        const AdjustedPaceIndex = activeAttrNames.value.indexOf('Adjusted_Pace');

        // 同时存在配速和等价配速图表
        if (PaceIndex >= 0 && AdjustedPaceIndex >= 0) {
          // // 等价配速不展示y轴
          // result[AdjustedPaceIndex].noYScale = true
          const min = d3.min([].concat(result[PaceIndex].ticks, result[AdjustedPaceIndex].ticks).map((item) => item.value));
          const max = d3.max([].concat(result[PaceIndex].ticks, result[AdjustedPaceIndex].ticks).map((item) => item.value));

          const gap = Math.floor((max - min) / 3);

          const ticksInfo = {
            ticks: [min, min + gap, min + 2 * gap, min + 3 * gap].reverse().map((val) => {
              return {
                value: val,
                text: result[PaceIndex].tickText(val)
              };
            })
          };

          ticksInfo.min = ticksInfo.ticks[0].value;
          ticksInfo.max = ticksInfo.ticks[ticksInfo.ticks.length - 1].value;

          Object.assign(result[PaceIndex], ticksInfo);
          Object.assign(result[AdjustedPaceIndex], ticksInfo);
        }
      })();

      // 速度和等价速度合并y轴
      (() => {
        const SpeedIndex = activeAttrNames.value.indexOf('Speed');
        const AdjustedSpeedIndex = activeAttrNames.value.indexOf('Adjusted_Speed');

        // 同时存在速度和等价速度图表
        if (SpeedIndex >= 0 && AdjustedSpeedIndex >= 0) {
          // // 等价速度不展示y轴
          // result[AdjustedSpeedIndex].noYScale = true
          const min = d3.min([].concat(result[SpeedIndex].ticks, result[AdjustedSpeedIndex].ticks).map((item) => item.value));
          const max = d3.max([].concat(result[SpeedIndex].ticks, result[AdjustedSpeedIndex].ticks).map((item) => item.value));

          const gap = Math.floor((max - min) / 3);

          const ticksInfo = {
            ticks: [min, min + gap, min + 2 * gap, min + 3 * gap].map((val) => {
              return {
                value: val,
                text: result[SpeedIndex].tickText(val)
              };
            })
          };

          ticksInfo.min = ticksInfo.ticks[0].value;
          ticksInfo.max = ticksInfo.ticks[ticksInfo.ticks.length - 1].value;

          Object.assign(result[SpeedIndex], ticksInfo);
          Object.assign(result[AdjustedSpeedIndex], ticksInfo);
        }
      })();
    }

    result = result.map((item, index) => {
      const attr = { ...item };
      const attrData = {};

      const maxNum = Math.round(clientWidth.value * 0.8);
      const lastIndex = chartData.value.length - 1;
      const { valueKey, data } = attr;
      if (!data && valueKey && valueKey !== NULL_TEXT) {
        attr.data = [];
        const supplyItem = [];
        chartData.value.forEach((d, i) => {
          if ([0, lastIndex].includes(i)) {
            supplyItem.push(d);
            return;
          }

          const level = d?.levelMap?.[valueKey] || 0;

          if (unitHelper.isNoValue(level)) {
            return;
          }
          // if(d[valueKey]==undefined) return;

          if (!attrData[level]) {
            attrData[level] = [];
          }
          attrData[level].push(d);
        });
        const levels = Reflect.ownKeys(attrData)
          .map((item) => Number(item))
          .sort((a, b) => b - a);

        for (let n = 0, length = levels.length; n < length; n++) {
          const levelData = attrData[levels[n]];
          const nextLevelData = attrData[levels[n + 1]];

          attr.data = attr.data.concat(levelData);

          if (!nextLevelData) {
            break;
          }
          if (attr.data.length >= maxNum) {
            break;
          }
        }

        attr.data = attr.data.concat(supplyItem);
        attr.data.sort((a, b) => {
          return a.timestamp - b.timestamp;
        });
      }

      return attr;
    });
    return result;
  });

  return {
    sportAttrs,
    updateActiveAttrNames,
    activeAttrNames,
    activeAttributes,
    chartAttr
  };
}
